import { Banner } from "components";
import React from "react";
import { useTranslation } from "react-i18next";

import "./PrivacyPolicy.styles.scss";

const WiredropPrivacyPolicy = () => {
	const { t } = useTranslation(["common"]);

	return (
		<div>
			<Banner title={t("common:menus.privacyPolicy")} />

			<div className="privacy-policy-wrapper">
				<div className="privacy-policy-content">
					<p className="effective-date">Effective Date: 29 April 2026</p>

					<p className="intro">
						This Privacy Policy explains how <strong>WireDrop</strong> collects, uses, stores, protects, and
						handles information when you install, access, or use the WireDrop application on Android, Windows,
						macOS, or any other supported system.
					</p>
					<p className="intro">
						The app is published by <strong>Eurycele Private Limited</strong> (&quot;we,&quot; &quot;our,&quot; or
						&quot;us&quot;). By installing, accessing, or using WireDrop, you agree to this Privacy Policy. If you
						do not agree, you must not install or use the application.
					</p>

					<section className="policy-section">
						<h2>1. About WireDrop</h2>
						<p>
							WireDrop is a local network communication application designed to allow users to discover nearby
							devices on the same network and exchange messages and files between supported devices.
						</p>
						<p>
							WireDrop is intended for local network usage. Communication between devices generally takes place
							over the same LAN/Wi-Fi network, depending on your device, operating system, network configuration,
							firewall settings, permissions, and app availability.
						</p>
					</section>

					<section className="policy-section">
						<h2>2. App Name and Naming Clarification</h2>
						<p>
							The name &quot;WireDrop&quot; is used by <strong>Eurycele Private Limited</strong> to refer to our
							application, product, and service, also referred to as <strong>Eurycele-WireDrop</strong>.
						</p>
						<p>
							If the name &quot;WireDrop&quot; is identical or similar to the name of any other application,
							project, service, product, company, brand, or third-party work, such similarity is purely
							coincidental and unintentional. Eurycele Private Limited does not claim affiliation, endorsement,
							partnership, sponsorship, ownership, or association with any third-party project, product, service,
							company, or brand using the same or a similar name, unless expressly stated in writing by Eurycele
							Private Limited.
						</p>
						<p>
							In the event of any naming conflict, confusion, legal concern, trademark concern, branding concern,
							or any other issue related to the name &quot;WireDrop,&quot; Eurycele Private Limited reserves the
							right, at its sole discretion, to modify, rename, rebrand, replace, or discontinue the use of the
							name &quot;WireDrop&quot; or &quot;Eurycele-WireDrop&quot; at any time, with or without prior
							notice.
						</p>
						<p>
							Continued use of the application after any such name change, rebranding, or policy update means
							that you accept the updated name, branding, and related terms.
						</p>
					</section>

					<section className="policy-section">
						<h2>3. Information We Collect</h2>
						<p>
							WireDrop is designed to work primarily on your local network. We do not intentionally collect
							personal information from users for advertising, profiling, or selling data.
						</p>
						<p>
							However, depending on your usage, device permissions, and operating system behavior, the app may
							process certain information locally, including but not limited to:
						</p>
						<ol>
							<li>
								<strong>Device name or hostname</strong> — used to identify your device to other WireDrop users
								on the same local network.
							</li>
							<li>
								<strong>Local IP address and network information</strong> — used for device discovery and local
								communication.
							</li>
							<li>
								<strong>Messages sent and received through the app</strong> — messages may be stored locally on
								your device to support chat history, unread messages, or conversation state.
							</li>
							<li>
								<strong>Files selected for transfer</strong> — files are processed only when you choose to send
								or receive them.
							</li>
							<li>
								<strong>App settings and local preferences</strong> — such as theme settings, notification
								preferences, device identity, unread state, and other local configuration data.
							</li>
							<li>
								<strong>Logs or diagnostic information</strong> — the app may generate local logs to help
								identify errors, crashes, or communication failures. These logs may include technical details
								such as timestamps, device state, network status, or error messages.
							</li>
						</ol>
					</section>

					<section className="policy-section">
						<h2>4. How We Use Information</h2>
						<p>Information processed by WireDrop is used for the following purposes:</p>
						<ol>
							<li>To discover nearby devices on the same local network.</li>
							<li>To send and receive messages.</li>
							<li>To send and receive files.</li>
							<li>To show notifications for incoming messages or transfers.</li>
							<li>To maintain local chat history and unread message status.</li>
							<li>To improve app reliability, stability, and user experience.</li>
							<li>To diagnose technical issues and resolve bugs.</li>
							<li>To preserve app settings and local preferences.</li>
						</ol>
						<p>
							We do not sell your personal information. We do not use your messages or files for advertising or
							user profiling.
						</p>
					</section>

					<section className="policy-section">
						<h2>5. Local Storage of Messages, Files, and App Data</h2>
						<p>
							WireDrop may store certain information locally on your device, including messages, chat history,
							file transfer status, settings, logs, and device-related information.
						</p>
						<p>
							You understand and agree that local data may be lost due to reasons including but not limited to:
						</p>
						<ol>
							<li>App uninstall or reinstall.</li>
							<li>Device reset or formatting.</li>
							<li>Operating system update or failure.</li>
							<li>Storage corruption.</li>
							<li>User deletion of app data.</li>
							<li>File cleanup tools.</li>
							<li>App crash or unexpected shutdown.</li>
							<li>Network or transfer failure.</li>
							<li>Software bugs or compatibility issues.</li>
							<li>Any other technical or non-technical reason.</li>
						</ol>
						<p>
							<strong>
								We are not responsible or liable for any loss of data, chat history, messages, files, transfer
								records, settings, logs, or any other information stored or processed by WireDrop.
							</strong>
						</p>
						<p>
							Users are responsible for maintaining their own backups of important messages, files, or
							information.
						</p>
					</section>

					<section className="policy-section">
						<h2>6. File Transfer Responsibility</h2>
						<p>
							WireDrop may allow users to send and receive files between devices on the same local network. You
							are solely responsible for:
						</p>
						<ol>
							<li>The files you choose to send.</li>
							<li>The files you choose to receive.</li>
							<li>Verifying the source and safety of received files.</li>
							<li>Ensuring that you have permission to share any file.</li>
							<li>
								Ensuring that transferred files do not violate any law, policy, copyright, license, or
								third-party rights.
							</li>
						</ol>
						<p>
							We are not responsible for any file loss, failed transfer, incomplete transfer, corrupted file,
							unauthorized file sharing, malware, harmful file, or any damage caused by files sent or received
							through WireDrop.
						</p>
					</section>

					<section className="policy-section">
						<h2>7. Network and Device Permissions</h2>
						<p>
							To function correctly, WireDrop may require certain permissions depending on the platform,
							including but not limited to:
						</p>
						<ol>
							<li>Local network access.</li>
							<li>Wi-Fi/network state access.</li>
							<li>Notification permission.</li>
							<li>File picker or storage access.</li>
							<li>Background activity permission.</li>
							<li>Device discovery-related permissions.</li>
						</ol>
						<p>
							These permissions are used only to provide the intended app functionality. If you deny required
							permissions, some features may not work correctly or may not work at all.
						</p>
					</section>

					<section className="policy-section">
						<h2>8. No Guarantee of Availability or Performance</h2>
						<p>
							WireDrop depends on several external factors, including your device, operating system, firewall,
							antivirus software, router, Wi-Fi network, LAN configuration, system permissions, background
							restrictions, and other installed software.
						</p>
						<p>
							We do not guarantee that WireDrop will always work without interruption, delay, error, data loss,
							message loss, file transfer failure, discovery failure, or compatibility issue.
						</p>
						<p>
							<strong>
								We are not responsible for any performance issue, system slowdown, network issue, device issue,
								app crash, operating system issue, hardware issue, software conflict, battery drain, data loss,
								chat loss, file loss, or any other direct or indirect issue caused by or associated with the
								installation or use of WireDrop.
							</strong>
						</p>
						<p>You use WireDrop at your own risk.</p>
					</section>

					<section className="policy-section">
						<h2>9. Message and Chat Delivery</h2>
						<p>
							WireDrop is designed to support local messaging between devices, but message delivery may depend on
							many technical conditions. We do not guarantee:
						</p>
						<ol>
							<li>That every message will be delivered.</li>
							<li>That messages will be delivered instantly.</li>
							<li>That chat history will always be preserved.</li>
							<li>That unread states will always be accurate.</li>
							<li>That notifications will always appear.</li>
							<li>That devices will always be discoverable.</li>
							<li>That messages will not be delayed, lost, duplicated, or corrupted.</li>
						</ol>
						<p>
							<strong>
								We are not responsible for any missed message, lost chat, deleted conversation, delayed message,
								failed notification, incorrect delivery status, or any communication loss.
							</strong>
						</p>
					</section>

					<section className="policy-section">
						<h2>10. Data Sharing</h2>
						<p>
							We do not sell, rent, or trade your personal data. We may disclose limited information only in the
							following situations:
						</p>
						<ol>
							<li>If required by law, regulation, court order, or government request.</li>
							<li>To protect our legal rights, users, systems, or app security.</li>
							<li>To investigate misuse, fraud, abuse, or unlawful activity.</li>
							<li>With your consent.</li>
						</ol>
						<p>
							Because WireDrop primarily works on a local network, information such as device name, hostname, or
							local network presence may be visible to other WireDrop users on the same network.
						</p>
					</section>

					<section className="policy-section">
						<h2>11. Third-Party Services</h2>
						<p>
							WireDrop may use third-party technologies, libraries, frameworks, operating system services, or
							platform services to provide app functionality.
						</p>
						<p>
							These third-party services may have their own privacy policies and terms. We are not responsible
							for the privacy practices, availability, performance, security, or behavior of any third-party
							service, library, operating system, platform, network, or device.
						</p>
					</section>

					<section className="policy-section">
						<h2>12. Security</h2>
						<p>
							We take reasonable steps to protect the app and its data. However, no software, system, network, or
							method of data transmission is completely secure.
						</p>
						<p>You understand and agree that:</p>
						<ol>
							<li>Local network communication may be affected by network security settings.</li>
							<li>Devices on the same network may be discoverable depending on app behavior and permissions.</li>
							<li>Unauthorized access may occur if your device, network, or system is compromised.</li>
							<li>
								We cannot guarantee absolute security of messages, files, local data, or network communication.
							</li>
						</ol>
						<p>You are responsible for securing your own device, network, operating system, and files.</p>
					</section>

					<section className="policy-section">
						<h2>13. User Responsibilities</h2>
						<p>By using WireDrop, you agree that you will:</p>
						<ol>
							<li>Use the app only for lawful purposes.</li>
							<li>
								Not use the app to send harmful, illegal, abusive, offensive, copyrighted, confidential, or
								unauthorized content.
							</li>
							<li>Not attempt to misuse, reverse engineer, attack, overload, or disrupt the app.</li>
							<li>Not use the app to harm other users, devices, systems, or networks.</li>
							<li>Verify files before opening or using them.</li>
							<li>Maintain backups of important data.</li>
							<li>Accept responsibility for your usage of the app.</li>
						</ol>
					</section>

					<section className="policy-section">
						<h2>14. Children&apos;s Privacy</h2>
						<p>WireDrop is not intended for use by children without appropriate supervision.</p>
						<p>
							We do not knowingly collect personal information from children. If you believe that a child has
							provided personal information through the app, please contact us so that we can take appropriate
							action.
						</p>
					</section>

					<section className="policy-section">
						<h2>15. Limitation of Liability</h2>
						<p>
							To the maximum extent permitted by applicable law, the developer, company, owners, employees,
							partners, affiliates, and representatives of WireDrop shall not be responsible or liable for any
							direct, indirect, incidental, special, consequential, or punitive damages arising from or related
							to:
						</p>
						<ol>
							<li>Use or inability to use the app.</li>
							<li>Message loss or chat loss.</li>
							<li>File loss or corrupted files.</li>
							<li>Data loss or local storage failure.</li>
							<li>Failed or incomplete file transfers.</li>
							<li>Network failure or discovery failure.</li>
							<li>Notification failure.</li>
							<li>Device slowdown or performance issues.</li>
							<li>Battery drain or resource usage.</li>
							<li>App crashes or system instability.</li>
							<li>Security incidents caused by user device, network, or third-party systems.</li>
							<li>Any other issue caused directly or indirectly by using WireDrop.</li>
						</ol>
						<p>Your use of WireDrop is at your own risk.</p>
					</section>

					<section className="policy-section">
						<h2>16. No Warranty</h2>
						<p>
							WireDrop is provided on an <strong>&quot;as is&quot;</strong> and{" "}
							<strong>&quot;as available&quot;</strong> basis. We make no warranties, express or implied,
							regarding:
						</p>
						<ol>
							<li>App availability.</li>
							<li>Accuracy.</li>
							<li>Performance.</li>
							<li>Reliability.</li>
							<li>Compatibility.</li>
							<li>Security.</li>
							<li>Fitness for a particular purpose.</li>
							<li>Error-free operation.</li>
							<li>Uninterrupted communication.</li>
							<li>Preservation of messages, chats, files, or data.</li>
						</ol>
						<p>
							We do not guarantee that WireDrop will meet your expectations or work perfectly on every device,
							network, or operating system.
						</p>
					</section>

					<section className="policy-section">
						<h2>17. Changes to This Privacy Policy</h2>
						<p>
							We reserve the right to update, modify, replace, or change this Privacy Policy at any time, with or
							without prior notice.
						</p>
						<p>
							Any changes will become effective when the updated Privacy Policy is published, displayed in the
							app, made available on our website, or otherwise communicated.
						</p>
						<p>
							By continuing to install, access, or use WireDrop after changes are made, you agree to the updated
							Privacy Policy. You are advised to review this Privacy Policy periodically.
						</p>
					</section>

					<section className="policy-section">
						<h2>18. Acceptance of This Privacy Policy</h2>
						<p>
							By downloading, installing, accessing, or using WireDrop on Android, Windows, macOS, or any other
							system, you confirm that:
						</p>
						<ol>
							<li>You have read this Privacy Policy.</li>
							<li>You understand this Privacy Policy.</li>
							<li>You agree to this Privacy Policy.</li>
							<li>
								You accept that WireDrop may process local device, network, message, file, and app-related data
								as described.
							</li>
							<li>
								You accept that we are not responsible for data loss, chat loss, file loss, app performance,
								system performance, or any issue caused directly or indirectly by use of the app.
							</li>
						</ol>
						<p>If you do not agree, you must not install, access, or use WireDrop.</p>
					</section>

					<section className="policy-section">
						<h2>19. Contact Us</h2>
						<p>For questions, support, or privacy-related concerns, contact us at:</p>
						<address>
							<strong>Eurycele Private Limited</strong>
							<br />
							Email:{" "}
							<a className="contact-link" href="mailto:connect@eurycele.com">
								connect@eurycele.com
							</a>
							<br />
							App: WireDrop
							<br />
							Country: India
						</address>
					</section>

					<section className="policy-section summary-section">
						<h2>Summary</h2>
						<p>
							WireDrop is a local network communication app. It does not collect personal information for
							advertising or profiling. Data such as device name, local IP, messages, files, and app settings are
							processed only on your device. We do not sell your data. You are responsible for your files,
							backups, and lawful use of the app. WireDrop is provided as-is with no guarantee of delivery,
							availability, or data preservation.
						</p>
					</section>
				</div>
			</div>
		</div>
	);
};

export default WiredropPrivacyPolicy;
